import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";

const track = (kind) => api.post("/events/reach-out", { kind }).catch(() => {});
import { Phone, MessageCircle, Globe, UserPlus, HeartHandshake } from "lucide-react";
import { Page, BrandHeader } from "@/components/Brand";
import { Btn } from "@/components/Btn";
import { usePrefs } from "@/lib/store";
import { calmMessage, defaultCrisisLine, smsUrl, telUrl } from "@/lib/support";

function Action({ icon: Icon, title, sub, href, testId, primary, kind }) {
  return (
    <a href={href} onClick={() => track(kind)} target={href.startsWith("http") ? "_blank" : undefined} rel="noreferrer" data-testid={testId}
      className={`flex items-center gap-4 rounded-[20px] p-4 transition-[transform,filter] hover:-translate-y-0.5 ${primary ? "bg-t-brand text-t-onbrand" : "tf-card text-t-on"}`}>
      <span className={`w-11 h-11 rounded-full grid place-items-center ${primary ? "bg-white/20" : "bg-t-brand3 text-t-brand"}`}><Icon size={20} /></span>
      <span><span className="block font-semibold">{title}</span>{sub ? <span className={`block text-[13px] ${primary ? "opacity-85" : "text-t-muted"}`}>{sub}</span> : null}</span>
    </a>
  );
}

export default function ReachOut() {
  const navigate = useNavigate();
  const { data: prefs } = usePrefs();
  const shown = useRef(false);
  useEffect(() => {
    if (shown.current) return;
    shown.current = true;
    track("shown");
  }, []);
  const contact = prefs?.safetyContact?.phone ? prefs.safetyContact : null;
  const line = prefs?.crisisLine?.number ? prefs.crisisLine : defaultCrisisLine();
  return (
    <Page>
      <BrandHeader />
      <div className="mt-10 tf-rise">
        <span className="w-14 h-14 rounded-full bg-t-brand3 text-t-brand grid place-items-center"><HeartHandshake size={28} /></span>
        <h1 className="mt-5 text-4xl font-bold text-t-on">You did really well</h1>
        <p className="mt-2 text-t-on2">Now that you've steadied yourself, it can help to connect with someone. Would you like to reach out?</p>
      </div>
      <p className="tf-tag mt-10 mb-3">Your person</p>
      {contact ? (
        <div className="flex flex-col gap-3">
          <Action primary icon={Phone} title={`Call ${contact.name}`} sub={contact.phone} href={telUrl(contact.phone)} testId="reach-call-contact" kind="contactCall" />
          <Action icon={MessageCircle} title={`Text ${contact.name}`} sub={`“${calmMessage(contact)}”`} href={smsUrl(contact.phone, calmMessage(contact))} testId="reach-text-contact" kind="contactText" />
        </div>
      ) : (
        <button onClick={() => navigate("/settings")} className="tf-card w-full flex items-center gap-3 p-4 text-t-brand font-semibold" data-testid="reach-add-contact">
          <UserPlus size={20} /> Add a trusted person in Settings →
        </button>
      )}
      <p className="tf-tag mt-8 mb-3">Support lines</p>
      <div className="flex flex-col gap-3">
        <Action icon={Phone} title={`Call ${line.name}`} sub={`${line.display || line.number} · 24/7`} href={telUrl(line.number)} testId="reach-call-crisis" kind="crisisCall" />
        {line.sms ? <Action icon={MessageCircle} title={`Text ${line.name}`} sub={line.sms} href={smsUrl(line.sms)} testId="reach-text-crisis" kind="crisisText" /> : null}
        <Action icon={Phone} title="Call Beyond Blue" sub="1300 22 4636 · 24/7 support" href={telUrl("1300224636")} testId="reach-call-beyondblue" kind="beyondBlue" />
        <Action icon={Globe} title="Black Dog Institute" sub="Mental health resources & tools" href="https://www.blackdoginstitute.org.au/" testId="reach-blackdog" kind="blackDog" />
      </div>
      <Btn variant="secondary" className="w-full mt-10" label="I'm okay for now" onClick={() => { track("okay"); navigate("/", { replace: true }); }} testId="reach-okay" />
    </Page>
  );
}
